import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { useApi, useFormSubmit, usePaginatedApi } from '../hooks/useApi';
import { reportsAPI, loadsAPI, billsAPI, paymentsAPI, systemConfigAPI, ddpsAPI, clientsAPI, transporteursAPI, oopsAPI, certificatesAPI } from '../services/api';
import { formatWeight, formatDate, loadHasWeightsForCharged, getLinkedClientsFromTruck } from '../utils/helpers';
import { paymentStatusI18nKey } from '../utils/paymentStatusI18n';
import LoadingSpinner, { CardLoader } from '../components/LoadingSpinner';
import Table from '../components/Table';
import Input from '../components/Input';
import MultiSelect from '../components/MultiSelect';
import {
  ScaleIcon,
  TruckIcon,
  BuildingOfficeIcon,
  ArchiveBoxIcon,
  TicketIcon,
  ChartBarIcon,
  ArrowUpIcon,
  ArrowDownIcon,
  ClockIcon,
  CheckCircleIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  ExclamationTriangleIcon,
  CheckBadgeIcon,
  EyeIcon,
} from '@heroicons/react/24/outline';
import FcfaIcon from '../components/FcfaIcon';
import GPSActivation from '../components/GPSActivation';
import Modal from '../components/Modal';
import Button from '../components/Button';
import LoadDetailViewModal from '../components/LoadDetailViewModal';
import LoadSupplementaryTaxModal from '../components/LoadSupplementaryTaxModal';
import { Navigate } from 'react-router-dom';

const DashboardPage = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const { user, isManagerOrAdmin, isAdmin, isOperator, isDriver, isAccountant, isPeseur, isSuper, getDriverTruck } = useAuth();
  const managerOrAdminScope = isManagerOrAdmin() && !isSuper();
  const managerOpsDashboard = managerOrAdminScope && !isAdmin();
  const { data: dashboardData, loading } = useApi(reportsAPI.getDashboard, [], { immediate: true });
  const [showTsModal, setShowTsModal] = useState(false);
  const [pendingLoadedStatusLoadId, setPendingLoadedStatusLoadId] = useState(null);
  const [driverLoadDetailOpen, setDriverLoadDetailOpen] = useState(false);
  const [driverLoadDetail, setDriverLoadDetail] = useState(null);
  const [driverLoadDetailLoading, setDriverLoadDetailLoading] = useState(false);

  // Check if tracking module is enabled
  const { data: trackingConfig } = useApi(
    () => systemConfigAPI.getByKey('module.tracking.enabled'),
    [],
    { showErrorToast: false, immediate: true }
  );

  const isTrackingEnabled = trackingConfig?.value === true || trackingConfig?.value === 'true';
  
  // Fetch pending DDPs for managers (not admins)
  const { data: pendingDdpsData, loading: pendingDdpsLoading } = useApi(
    managerOpsDashboard
      ? async () => {
          const ddps = await ddpsAPI.getMy();
          return { data: { ddps } };
        }
      : null,
    [],
    { immediate: managerOpsDashboard }
  );
  
  const filteredPendingDdps = React.useMemo(() => {
    // Handle both array and object response formats
    const ddps = Array.isArray(pendingDdpsData) ? pendingDdpsData : (pendingDdpsData?.ddps || []);
    return ddps.filter(ddp => ddp.status === 'pending');
  }, [pendingDdpsData]);

  const { data: clientDdpsData, loading: clientDdpsLoading } = useApi(
    isOperator()
      ? async () => {
          const ddps = await ddpsAPI.getMy();
          return { data: { ddps } };
        }
      : null,
    [],
    { immediate: isOperator() }
  );

  const clientDashboardDdps = React.useMemo(() => {
    const ddps = Array.isArray(clientDdpsData) ? clientDdpsData : (clientDdpsData?.ddps || []);
    return ddps.filter((ddp) => ddp.status === 'pending' || ddp.status === 'denied');
  }, [clientDdpsData]);

  const { data: pendingOopsData, loading: pendingOopsLoading } = useApi(
    managerOpsDashboard
      ? async () => {
          const result = await oopsAPI.getMy();
          return { data: result };
        }
      : null,
    [],
    { immediate: managerOpsDashboard }
  );

  const dashboardOops = React.useMemo(() => {
    const oops = pendingOopsData?.oops || pendingOopsData?.data || [];
    return Array.isArray(oops) ? oops : [];
  }, [pendingOopsData]);

  const { data: pendingCertificatesData, loading: pendingCertificatesLoading } = useApi(
    managerOpsDashboard
      ? () => certificatesAPI.getAll({ limit: 5 })
      : null,
    [],
    { immediate: managerOpsDashboard }
  );

  const pendingCertificates = React.useMemo(() => {
    const certificates = pendingCertificatesData?.certificates || pendingCertificatesData?.data || [];
    return Array.isArray(certificates)
      ? certificates.filter((certificate) => String(certificate.status || '').toUpperCase() === 'PENDING')
      : [];
  }, [pendingCertificatesData]);

  const { data: peseurCertificatesData, loading: peseurCertificatesLoading } = useApi(
    isPeseur()
      ? () => certificatesAPI.getAll({ limit: 1000 })
      : null,
    [user?.id],
    { immediate: isPeseur() }
  );

  const peseurCertificatesNeedingAttention = React.useMemo(() => {
    const certificates = peseurCertificatesData?.certificates || peseurCertificatesData?.data || [];
    return Array.isArray(certificates)
      ? certificates.filter((certificate) => {
          const status = String(certificate.status || '').toUpperCase();
          return status === 'PENDING' || status === 'REJECTED';
        })
      : [];
  }, [peseurCertificatesData]);
  
  // Fetch pending clients from clients API
  const { refetch: refetchPendingClients } = useApi(
    managerOrAdminScope ? () => clientsAPI.getAll({ limit: 1000 }) : null,
    [],
    { immediate: managerOrAdminScope }
  );

  const { data: allTransporteursData, refetch: refetchTransporteurs, loading: transporteursLoading } = useApi(
    managerOrAdminScope ? () => transporteursAPI.getAll({ limit: 1000 }) : null,
    [],
    { immediate: managerOrAdminScope }
  );

  const transporteurOptions = React.useMemo(() => {
    const list = allTransporteursData?.transporteurs || allTransporteursData?.data || [];
    return list.map(transporteur => ({
      value: transporteur.id,
      label: transporteur.companyName || transporteur.contactName || transporteur.id
    }));
  }, [allTransporteursData]);
  
  // Accountant and client-specific data (bills/payments)
  const { data: billsStats, loading: billsLoading } = useApi(
    ((isAccountant() && !isPeseur()) || isOperator()) ? billsAPI.getStats : null, 
    [], 
    { immediate: ((isAccountant() && !isPeseur()) || isOperator()) }
  );
  const { data: paymentsStats, loading: paymentsLoading } = useApi(
    (isAccountant() && !isPeseur()) ? paymentsAPI.getStats : null, 
    [], 
    { immediate: (isAccountant() && !isPeseur()) }
  );
  const { data: recentBillsData, loading: recentBillsLoading } = useApi(
    ((isAccountant() && !isPeseur()) || isOperator()) ? () => billsAPI.getRecent({ limit: 5 }) : null, 
    [], 
    { immediate: ((isAccountant() && !isPeseur()) || isOperator()) }
  );
  const { data: recentPaymentsData, loading: recentPaymentsLoading } = useApi(
    (isAccountant() && !isPeseur()) ? () => paymentsAPI.getRecent({ limit: 5 }) : null, 
    [], 
    { immediate: (isAccountant() && !isPeseur()) }
  );

  const { data: requestedLoadsData, loading: requestedLoadsLoading, refetch: refetchRequestedLoads } = useApi(
    (isOperator() || managerOpsDashboard)
      ? async () => {
          const params = {
            status: 'REQUESTED',
            limit: 5
          };
          if (isOperator()) {
            params.operatorId = user?.id;
          }
          return loadsAPI.getAll(params);
        }
      : null,
    [user?.id],
    { immediate: (isOperator() || managerOpsDashboard) }
  );

  const requestedLoads = React.useMemo(() => {
    const loads = requestedLoadsData?.loads || requestedLoadsData?.data || [];
    return Array.isArray(loads) ? loads : [];
  }, [requestedLoadsData]);

  const { data: peseurLoadsData, loading: peseurLoadsLoading } = useApi(
    isPeseur()
      ? () => loadsAPI.getAll({ limit: 1000 })
      : null,
    [user?.id],
    { immediate: isPeseur() }
  );

  const peseurLoads = React.useMemo(() => {
    const loads = peseurLoadsData?.loads || peseurLoadsData?.data || [];
    return Array.isArray(loads) ? loads : [];
  }, [peseurLoadsData]);

  const peseurRequestedLoads = React.useMemo(() => {
    return peseurLoads
      .filter((load) => String(load?.status || '').toUpperCase() === 'REQUESTED')
      .sort((a, b) => new Date(b?.loadDate || 0).getTime() - new Date(a?.loadDate || 0).getTime())
      .slice(0, 5);
  }, [peseurLoads]);
  
  // Driver loads management
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedPendingClient, setSelectedPendingClient] = useState(null);
  const [selectedPendingClientDetail, setSelectedPendingClientDetail] = useState(null);
  const [isClientApprovalModalOpen, setIsClientApprovalModalOpen] = useState(false);
  const [createUserOnApproval, setCreateUserOnApproval] = useState(true);
  const loadingPendingClientDetail = false;
  const [selectedTransporteurIds, setSelectedTransporteurIds] = useState([]);
  const [generateTransporteurLoading, setGenerateTransporteurLoading] = useState(false);

  // Fetch driver loads if user is a driver - show only loads for driver's assigned truck
  const driverTruck = getDriverTruck();
  const shouldFetchLoads = isDriver() && driverTruck?.id;
  
  //console.log('DEBUG: isDriver():', isDriver(), 'driverTruck:', driverTruck, 'shouldFetchLoads:', shouldFetchLoads);
  
  // Build parameters object for driver loads
  const driverLoadFilters = React.useMemo(() => {
    if (!shouldFetchLoads) return {};
    
    const params = { 
      limit: 10, // Show only 10 loads on dashboard
      search: searchTerm,
      truckId: driverTruck?.id // Filter by driver's assigned truck
    };
    
    // For "My loads in progress", exclude delivered loads unless specifically filtered
    if (statusFilter) {
      params.status = statusFilter;
      // Don't include excludeDelivered when filtering by specific status
    } else {
      // When no status filter is applied, exclude delivered loads
      params.excludeDelivered = 'true';
      // Don't include status when excluding delivered
    }
    
    console.log('DEBUG: Driver dashboard filters:', JSON.stringify(params, null, 2));
    return params;
  }, [searchTerm, statusFilter, driverTruck?.id, shouldFetchLoads]);
  
  // Use usePaginatedApi for driver loads if the user is a driver
  const driverLoadsResult = usePaginatedApi(
    shouldFetchLoads ? loadsAPI.getAll : null, 
    shouldFetchLoads ? driverLoadFilters : {}
  );
  
  const driverLoadsData = shouldFetchLoads ? driverLoadsResult.data : [];
  const loadsLoading = shouldFetchLoads ? driverLoadsResult.loading : false;
  const refetchLoads = shouldFetchLoads ? driverLoadsResult.refresh : () => {};
  
  //console.log('DEBUG: driverLoadsData:', driverLoadsData);

  // Update load status
  const { submit: updateLoadStatus, loading: updateLoading } = useFormSubmit(
    ({ loadId, status, tsOptionCode, tsHours }) => loadsAPI.updateStatus(loadId, {
      status,
      tsOptionCode,
      tsHours
    }),
    {
      onSuccess: () => {
        refetchLoads();
      },
      onError: (error, { status }) => {
        // Handle specific validation errors for LOADED status
        if (error.response?.status === 400 && status === 'LOADED') {
          const errorMessage = error.response.data?.message;
          const details = error.response.data?.details;
          
          if (details) {
            const missing = [];
            if (details.emptyWeightMissing) missing.push('empty weight');
            if (details.loadedWeightMissing) missing.push('loaded weight');
            
            if (missing.length > 0) {
              return `Cannot mark as LOADED: Missing ${missing.join(' and ')}. Please edit the load to add the missing weight information.`;
            }
          }
          return errorMessage;
        }
        return null; // Use default error handling
      },
      successMessage: 'Load status updated successfully'
    }
  );

  const { submit: approveClient, loading: approvingClient } = useFormSubmit(
    ({ clientId, transporteurIds, createUser }) => clientsAPI.update(clientId, { status: 'active', transporteurIds, createUser }),
    {
      onSuccess: () => {
        setIsClientApprovalModalOpen(false);
        setSelectedPendingClient(null);
        setSelectedPendingClientDetail(null);
        setSelectedTransporteurIds([]);
        setCreateUserOnApproval(true);
        refetchPendingClients();
      },
      successMessage: t('dashboard.clientApprovedSuccess'),
    }
  );

  const handleApproveClient = React.useCallback(() => {
    if (!selectedPendingClient?.id) return;
    const hasPendingPassword = selectedPendingClientDetail?.hasPendingPassword || selectedPendingClient?.hasPendingPassword;
    approveClient({
      clientId: selectedPendingClient.id,
      transporteurIds: selectedTransporteurIds,
      createUser: createUserOnApproval && hasPendingPassword
    });
  }, [approveClient, selectedPendingClient, selectedPendingClientDetail, selectedTransporteurIds, createUserOnApproval]);

  const handleGenerateTransporteurFromClient = React.useCallback(async () => {
    const client = selectedPendingClientDetail || selectedPendingClient;
    if (!client) return;

    if (!client.companyName?.trim() || !client.contactName?.trim() || !client.contactEmail?.trim() || !client.contactPhone?.trim() || !client.address?.trim()) {
      toast.error(t('clients.fillRequired'));
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(client.contactEmail)) {
      toast.error(t('clients.validEmail'));
      return;
    }

    setGenerateTransporteurLoading(true);
    try {
      const payload = {
        companyName: client.companyName.trim(),
        address: client.address.trim(),
        contactName: client.contactName.trim(),
        contactEmail: client.contactEmail.trim(),
        contactPhone: client.contactPhone.trim(),
        status: 'active',
        clientIds: [client.id]
      };

      const response = await transporteursAPI.create(payload);
      const createdTransporteur = response?.data?.transporteur;
      if (createdTransporteur?.id) {
        setSelectedTransporteurIds(prev => Array.from(new Set([...(prev || []), createdTransporteur.id])));
        await refetchTransporteurs();
        toast.success(t('clients.transporteurGenerated'));
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || t('clients.transporteurGenerateError'));
    } finally {
      setGenerateTransporteurLoading(false);
    }
  }, [selectedPendingClientDetail, selectedPendingClient, t, toast, refetchTransporteurs]);

  // Helper functions for driver loads table - memoized to prevent recreation
  const getStatusBadgeColor = React.useCallback((status) => {
    if (!status) return 'bg-gray-100 text-gray-800';
    switch (status.toString().toUpperCase()) {
      case 'REQUESTED': return 'bg-yellow-100 text-yellow-800';
      case 'REJECTED': return 'bg-red-100 text-red-800';
      case 'ACCEPTED': return 'bg-blue-100 text-blue-800';
      case 'LOADED': return 'bg-purple-100 text-purple-800';
      case 'DELIVERED': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  }, []);

  const getAvailableStatuses = React.useCallback((currentStatus, load = null) => {
    if (!currentStatus) return [];
    
    // Drivers can only change status from LOADED to DELIVERED
    if (isDriver()) {
      switch (currentStatus.toString().toUpperCase()) {
        case 'LOADED': return ['DELIVERED'];
        default: return [];
      }
    }
    
    // Default status transitions for other roles
    switch (currentStatus.toString().toUpperCase()) {
      case 'REQUESTED': return ['ACCEPTED', 'REJECTED'];
      case 'REJECTED': return ['REQUESTED', 'ACCEPTED'];
      case 'ACCEPTED':
        if (load && !loadHasWeightsForCharged(load)) return ['REJECTED'];
        return ['LOADED', 'REJECTED'];
      case 'LOADED': return ['DELIVERED'];
      case 'DELIVERED': return [];
      default: return [];
    }
  }, [isDriver]);

  const handleStatusUpdate = React.useCallback((row, newStatus) => {
    if (!row?.id || !newStatus) return;

    if (newStatus === 'LOADED') {
      if (!loadHasWeightsForCharged(row)) {
        toast.error(t('loads.weightsRequiredBeforeCharged'));
        return;
      }
      setPendingLoadedStatusLoadId(row.id);
      setShowTsModal(true);
      return;
    }

    updateLoadStatus({ loadId: row.id, status: newStatus });
  }, [updateLoadStatus, toast, t]);

  const handleDashboardTsConfirm = React.useCallback(({ tsOptionCode, tsHours }) => {
    if (!pendingLoadedStatusLoadId) return;

    updateLoadStatus({
      loadId: pendingLoadedStatusLoadId,
      status: 'LOADED',
      tsOptionCode,
      tsHours
    });

    setPendingLoadedStatusLoadId(null);
    setShowTsModal(false);
  }, [pendingLoadedStatusLoadId, updateLoadStatus]);

  const handleDashboardTsClose = React.useCallback(() => {
    setPendingLoadedStatusLoadId(null);
    setShowTsModal(false);
  }, []);

  const openDriverLoadDetail = React.useCallback(async (row) => {
    if (!row?.id) return;
    setDriverLoadDetail(null);
    setDriverLoadDetailOpen(true);
    setDriverLoadDetailLoading(true);
    try {
      const response = await loadsAPI.getById(row.id);
      setDriverLoadDetail(response.data.load);
    } catch (e) {
      toast.error(e.response?.data?.message || t('dashboard.loadDetailsFetchError'));
      setDriverLoadDetailOpen(false);
    } finally {
      setDriverLoadDetailLoading(false);
    }
  }, [toast, t]);

  const handleSearch = React.useCallback((e) => {
    const newSearchTerm = e.target.value || '';
    console.log('DEBUG: Search term changed to:', newSearchTerm);
    setSearchTerm(newSearchTerm);
    
    // Update the API params immediately for better responsiveness
    if (shouldFetchLoads && driverLoadsResult.updateParams) {
      const params = { 
        limit: 10,
        search: newSearchTerm,
        truckId: driverTruck?.id
      };
      
      if (statusFilter) {
        params.status = statusFilter;
      } else {
        params.excludeDelivered = 'true';
      }
      
      driverLoadsResult.updateParams(params);
    }
  }, [shouldFetchLoads, driverLoadsResult, statusFilter, driverTruck]);

  const handleStatusFilter = React.useCallback((e) => {
    const newStatusFilter = e.target.value || '';
    console.log('DEBUG: Status filter changed to:', newStatusFilter || 'All In Progress');
    setStatusFilter(newStatusFilter);
    
    // Update the API params immediately for better responsiveness
    if (shouldFetchLoads && driverLoadsResult.updateParams) {
      const params = { 
        limit: 10,
        search: searchTerm,
        truckId: driverTruck?.id,
        page: 1 // Reset to first page when changing filters
      };
      
      if (newStatusFilter) {
        params.status = newStatusFilter;
        // Explicitly remove excludeDelivered when setting specific status
        params.excludeDelivered = undefined;
      } else {
        params.excludeDelivered = 'true';
        // Explicitly remove status when showing all in progress
        params.status = undefined;
      }
      
      console.log('DEBUG: Immediately updating params for status filter:', JSON.stringify(params, null, 2));
      driverLoadsResult.updateParams(params);
    }
  }, [shouldFetchLoads, driverLoadsResult, searchTerm, driverTruck]);

  // The filtering is now handled by driverLoadFilters useMemo and usePaginatedApi
  // No additional useEffect needed as usePaginatedApi will automatically update when driverLoadFilters changes

  // Driver loads table columns - memoized to prevent recreation
  const driverLoadsColumns = React.useMemo(() => [
    {
      key: 'loadInfo',
      title: t('dashboard.loadInformation'),
      render: (value, row) => (
        <button
          type="button"
          onClick={() => openDriverLoadDetail(row)}
          className="w-full text-left rounded-md p-1 -m-1 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-0 group"
        >
          <div className="flex items-start gap-2">
            <EyeIcon className="h-4 w-4 text-blue-600 flex-shrink-0 mt-0.5 opacity-80 group-hover:opacity-100" aria-hidden />
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium text-gray-900 group-hover:text-blue-900">
                {row?.client?.companyName || t('dashboard.unknownClient')}
              </div>
              <div className="text-sm text-gray-500">
                {row?.product?.name || t('dashboard.unknownProduct')} • {row?.loadDate ? formatDate(row.loadDate) : t('dashboard.noDate')}
              </div>
              <div className="text-xs text-blue-600 mt-0.5 font-medium">
                {t('dashboard.viewLoadDetails')}
              </div>
            </div>
          </div>
        </button>
      )
    },
    {
      key: 'weight',
      title: t('dashboard.weight'),
      render: (value, row) => (
        <div>
          <div className="text-sm font-medium text-gray-900">
            {row?.netWeight ? formatWeight(row.netWeight, 'ton', { noUnit: true }) : '-'}
          </div>
          <div className="text-xs text-gray-500">
            {row?.transactionType || '-'}
          </div>
        </div>
      )
    },
    {
      key: 'location',
      title: t('dashboard.location'),
      render: (value, row) => (
        <div className="text-sm text-gray-900">
          <div>{row?.port?.name || t('dashboard.unknownPort')}</div>
          {row?.mole?.name && (
            <div className="text-xs text-gray-500">{row.mole.name}</div>
          )}
        </div>
      )
    },
    {
      key: 'status',
      title: t('dashboard.status'),
      render: (value, row) => (
        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadgeColor(row?.status)}`}>
          {row?.status ? t(`loads.statuses.${row.status.toLowerCase()}`) : t('dashboard.unknown')}
        </span>
      )
    },
    {
      key: 'actions',
      title: t('dashboard.actions'),
      render: (value, row) => {
        const availableStatuses = getAvailableStatuses(row.status, row);
        return (
          <div className="flex space-x-2">
            {availableStatuses.map(status => (
              <button
                key={status}
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleStatusUpdate(row, status);
                }}
                disabled={updateLoading}
                className="inline-flex items-center px-2 py-1 text-xs font-medium rounded border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <CheckCircleIcon className="h-3 w-3 mr-1" />
                {updateLoading ? '...' : t(`loads.statuses.${status.toLowerCase()}`)}
              </button>
            ))}
            {/* Leave blank when no actions are available */}
          </div>
        );
      }
    }
  ], [updateLoading, getStatusBadgeColor, getAvailableStatuses, handleStatusUpdate, openDriverLoadDetail, t]);

  const StatCard = ({ title, value, icon: Icon, change, changeType, color = 'blue' }) => {
    const colorClasses = {
      blue: 'bg-blue-500',
      green: 'bg-green-500',
      yellow: 'bg-yellow-500',
      orange: 'bg-orange-500',
      red: 'bg-red-500',
      indigo: 'bg-indigo-500',
      purple: 'bg-purple-500',
    };

    return (
      <div className="card">
        <div className="flex items-center">
          <div className="flex-shrink-0">
            <div className={`p-3 rounded-md ${colorClasses[color]}`}>
              <Icon className="h-6 w-6 text-white" />
            </div>
          </div>
          <div className="ml-5 w-0 flex-1">
            <dl>
              <dt className="text-sm font-medium text-gray-500 leading-tight">
                {title}
              </dt>
              <dd className="text-lg font-medium text-gray-900">
                {value}
              </dd>
              {change !== undefined && (
                <dd className="flex items-center text-sm">
                  {changeType === 'increase' ? (
                    <ArrowUpIcon className="flex-shrink-0 h-4 w-4 text-green-500" />
                  ) : (
                    <ArrowDownIcon className="flex-shrink-0 h-4 w-4 text-red-500" />
                  )}
                  <span className={`ml-1 ${changeType === 'increase' ? 'text-green-600' : 'text-red-600'}`}>
                    {Math.abs(change).toFixed(1)}%
                  </span>
                  <span className="text-gray-500 ml-1">{t('dashboard.vsYesterday')}</span>
                </dd>
              )}
            </dl>
          </div>
        </div>
      </div>
    );
  };

  const QuickAction = ({ title, description, to, icon: Icon, color = 'blue' }) => {
    const colorClasses = {
      blue: 'text-blue-600 bg-blue-100 hover:bg-blue-200',
      green: 'text-green-600 bg-green-100 hover:bg-green-200',
      yellow: 'text-yellow-600 bg-yellow-100 hover:bg-yellow-200',
      red: 'text-red-600 bg-red-100 hover:bg-red-200',
    };

    return (
      <Link
        to={to}
        className="group block p-6 bg-white rounded-lg border border-gray-200 hover:border-gray-300 hover:shadow-md transition-all"
      >
        <div className="flex items-center">
          <div className={`p-3 rounded-lg ${colorClasses[color]} transition-colors`}>
            <Icon className="h-6 w-6" />
          </div>
          <div className="ml-4">
            <h3 className="text-lg font-medium text-gray-900 group-hover:text-gray-600">
              {title}
            </h3>
            <p className="text-sm text-gray-500">
              {description}
            </p>
          </div>
        </div>
      </Link>
    );
  };

  const stats = dashboardData || {};
  const startOfToday = React.useMemo(() => {
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    return date;
  }, []);
  const startOfYesterday = React.useMemo(() => {
    const date = new Date(startOfToday);
    date.setDate(date.getDate() - 1);
    return date;
  }, [startOfToday]);

  const peseurStats = React.useMemo(() => {
    const summary = {
      todayLoads: 0,
      yesterdayLoads: 0,
      todayWeight: 0,
      yesterdayWeight: 0,
      activeTrucks: 0,
      recentLoads: []
    };

    const truckIds = new Set();
    const recentLoads = [...peseurLoads]
      .filter((load) => ['LOADED', 'DELIVERED'].includes(String(load?.status || '').toUpperCase()))
      .sort((a, b) => new Date(b.loadDate).getTime() - new Date(a.loadDate).getTime())
      .slice(0, 5)
      .map((load) => ({
        id: load.id,
        truck: load.truck?.plateNumber || '—',
        client: load.client?.companyName || '—',
        product: load.product?.name || '—',
        time: load.loadDate ? formatDate(load.loadDate) : '—',
        weight: load.netWeight || 0,
        type: load.transactionType || '—',
        status: load.status
      }));

    peseurLoads.forEach((load) => {
      if (load.truckId) {
        truckIds.add(load.truckId);
      }

      const loadDate = load.loadDate ? new Date(load.loadDate) : null;
      if (!loadDate || Number.isNaN(loadDate.getTime())) return;

      const netWeight = Number(load.netWeight || 0);
      if (loadDate >= startOfToday) {
        summary.todayLoads += 1;
        summary.todayWeight += netWeight;
      } else if (loadDate >= startOfYesterday && loadDate < startOfToday) {
        summary.yesterdayLoads += 1;
        summary.yesterdayWeight += netWeight;
      }
    });

    summary.activeTrucks = truckIds.size;
    summary.recentLoads = recentLoads;
    return summary;
  }, [peseurLoads, startOfToday, startOfYesterday]);

  const dashboardRecentWeighedLoads = React.useMemo(() => {
    const all = stats.recentLoads || [];
    return all.filter((load) => ['LOADED', 'DELIVERED'].includes(String(load?.status || '').toUpperCase()));
  }, [stats.recentLoads]);

  const todayLoads = isPeseur() ? peseurStats.todayLoads : (stats.todayStats?.loads || 0);
  const yesterdayLoads = isPeseur() ? peseurStats.yesterdayLoads : (stats.yesterdayStats?.loads || 0);
  const todayWeight = isPeseur() ? peseurStats.todayWeight : (stats.todayStats?.weight || 0);
  const yesterdayWeight = isPeseur() ? peseurStats.yesterdayWeight : (stats.yesterdayStats?.weight || 0);

  // Redirect SUPER users to their specialized dashboard
  if (isSuper()) {
    return <Navigate to="/super-dashboard" replace />;
  }

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <CardLoader key={i} />
          ))}
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <CardLoader />
          <CardLoader />
        </div>
      </div>
    );
  }



  const loadsChange = yesterdayLoads > 0 ? ((todayLoads - yesterdayLoads) / yesterdayLoads) * 100 : 0;
  const weightChange = yesterdayWeight > 0 ? ((todayWeight - yesterdayWeight) / yesterdayWeight) * 100 : 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">
          {isDriver() ? t('dashboard.driverTitle') : 
           isAccountant() ? t('dashboard.accountantTitle') : 
           t('dashboard.title')}
        </h1>
        <p className="text-gray-600">
          {isDriver() ? t('dashboard.driverSubtitle') : 
           isAccountant() ? t('dashboard.accountantSubtitle') : 
           t('dashboard.subtitle')}
        </p>
        {/* Driver truck info */}
        {isDriver() && getDriverTruck() && (
          <div className="mt-3 flex items-center text-sm text-gray-600">
            <TruckIcon className="h-4 w-4 mr-2" />
            <span className="font-medium">
              {getDriverTruck().plateNumber || getDriverTruck().id}
              {(() => {
                const clients = getLinkedClientsFromTruck(getDriverTruck());
                const label = clients.map((c) => c.companyName).filter(Boolean).join(' • ');
                return label ? ` • ${label}` : '';
              })()}
            </span>
          </div>
        )}
      </div>

      {/* Quick Stats */}
      {(isAccountant() && !isPeseur()) ? (
        // Accountant-specific financial dashboard
        <div className="space-y-6">
          {/* Bills Summary */}
          <div>
            <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('dashboard.billsSummary')}</h2>
            {billsLoading ? (
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
                <CardLoader />
                <CardLoader />
                <CardLoader />
                <CardLoader />
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-5">
                <StatCard
                  title={t('dashboard.totalBills')}
                  value={billsStats?.stats?.totalBills || 0}
                  icon={FcfaIcon}
                  color="blue"
                />
                <StatCard
                  title={t('dashboard.pendingBills')}
                  value={billsStats?.stats?.pendingBills || 0}
                  icon={ClockIcon}
                  color="yellow"
                />
                <StatCard
                  title={t('dashboard.billsWaitingApproval')}
                  value={billsStats?.stats?.billsWaitingApproval || 0}
                  icon={ExclamationTriangleIcon}
                  color="orange"
                />
                <StatCard
                  title={t('dashboard.paidBills')}
                  value={billsStats?.stats?.paidBills || 0}
                  icon={CheckCircleIcon}
                  color="green"
                />
                <StatCard
                  title={t('dashboard.totalBillAmount')}
                  value={new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'XOF' }).format(billsStats?.stats?.totalAmount || 0)}
                  icon={FcfaIcon}
                  color="purple"
                />
              </div>
            )}
          </div>

          {/* Payments Summary */}
          <div>
            <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('dashboard.paymentsSummary')}</h2>
            {paymentsLoading ? (
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
                <CardLoader />
                <CardLoader />
                <CardLoader />
                <CardLoader />
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
                <StatCard
                  title={t('dashboard.totalPayments')}
                  value={paymentsStats?.stats?.totalPayments || 0}
                  icon={FcfaIcon}
                  color="blue"
                />
                <StatCard
                  title={t('dashboard.pendingPayments')}
                  value={paymentsStats?.stats?.pendingPayments || 0}
                  icon={ClockIcon}
                  color="yellow"
                />
                <StatCard
                  title={t('dashboard.completedPayments')}
                  value={paymentsStats?.stats?.completedPayments || 0}
                  icon={CheckCircleIcon}
                  color="green"
                />
                <StatCard
                  title={t('dashboard.totalPaymentAmount')}
                  value={new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'XOF' }).format(paymentsStats?.stats?.totalAmount || 0)}
                  icon={FcfaIcon}
                  color="purple"
                />
              </div>
            )}
          </div>

          {/* Quick Actions */}
          <div>
            <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('dashboard.quickActions')}</h2>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <Link
                to="/bills"
                className="block p-6 bg-white rounded-lg border border-gray-200 hover:border-blue-300 hover:shadow-md transition-all"
              >
                <div className="flex items-center">
                  <FcfaIcon className="h-8 w-8 text-blue-600 mr-3" />
                  <div>
                    <h3 className="text-lg font-medium text-gray-900">{t('navigation.bills')}</h3>
                    <p className="text-sm text-gray-500">{t('dashboard.manageBills')}</p>
                  </div>
                </div>
              </Link>
              
              <Link
                to="/payments"
                className="block p-6 bg-white rounded-lg border border-gray-200 hover:border-green-300 hover:shadow-md transition-all"
              >
                <div className="flex items-center">
                  <CheckCircleIcon className="h-8 w-8 text-green-600 mr-3" />
                  <div>
                    <h3 className="text-lg font-medium text-gray-900">{t('navigation.payments')}</h3>
                    <p className="text-sm text-gray-500">{t('dashboard.processPayments')}</p>
                  </div>
                </div>
              </Link>
              
              <Link
                to="/certificates"
                className="block p-6 bg-white rounded-lg border border-gray-200 hover:border-purple-300 hover:shadow-md transition-all"
              >
                <div className="flex items-center">
                  <TicketIcon className="h-8 w-8 text-purple-600 mr-3" />
                  <div>
                    <h3 className="text-lg font-medium text-gray-900">{t('navigation.certificates')}</h3>
                    <p className="text-sm text-gray-500">{t('dashboard.viewCertificates')}</p>
                  </div>
                </div>
              </Link>
            </div>
          </div>
        </div>
      ) : isDriver() ? (
        // Driver-specific stats
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          <StatCard
            title={t('dashboard.myTruckLoads')}
            value={todayLoads.toLocaleString()}
            icon={ArchiveBoxIcon}
            change={loadsChange}
            changeType={loadsChange >= 0 ? 'increase' : 'decrease'}
            color="blue"
          />
          
          <StatCard
            title={t('dashboard.totalWeight')}
            value={formatWeight(todayWeight, 'ton', { noUnit: true })}
            icon={ScaleIcon}
            change={weightChange}
            changeType={weightChange >= 0 ? 'increase' : 'decrease'}
            color="green"
          />
          
          <StatCard
            title={t('dashboard.truckInformation')}
            value={getDriverTruck()?.plateNumber || '-'}
            icon={TruckIcon}
            color="purple"
          />
        </div>
      ) : (
        // Admin/Manager/Client stats
        <div className={`grid grid-cols-1 gap-5 sm:grid-cols-2 ${isPeseur() ? 'lg:grid-cols-3' : 'lg:grid-cols-4'}`}>
          <StatCard
            title={t('dashboard.totalLoads')}
            value={todayLoads.toLocaleString()}
            icon={ArchiveBoxIcon}
            change={loadsChange}
            changeType={loadsChange >= 0 ? 'increase' : 'decrease'}
            color="blue"
          />
          
          <StatCard
            title={t('dashboard.totalWeight')}
            value={formatWeight(todayWeight, 'ton', { noUnit: true })}
            icon={ScaleIcon}
            change={weightChange}
            changeType={weightChange >= 0 ? 'increase' : 'decrease'}
            color="green"
          />
          
          <StatCard
            title={t('dashboard.activeTrucks')}
            value={isPeseur() ? peseurStats.activeTrucks : (stats.totals?.trucks || 0)}
            icon={TruckIcon}
            color="indigo"
          />

          {!isPeseur() && (
            <StatCard
              title={t('dashboard.pendingBills')}
              value={stats.totals?.pendingBills || 0}
              icon={FcfaIcon}
              color={stats.totals?.pendingBills > 0 ? 'yellow' : 'green'}
            />
          )}
        </div>
      )}

      <div className={`grid grid-cols-1 ${isDriver() ? '' : 'lg:grid-cols-2'} gap-6`}>
        {/* Recent Loads/Bills - Driver Table, Accountant/Client Bills, or Regular Loads */}
        {(isAccountant() || isOperator()) ? (
          // Accountant/Client recent loads/bills and payments - two column layout
          <>
            {isOperator() && (requestedLoadsLoading || requestedLoads.length > 0) && (
            <div className="card">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-medium text-gray-900">{t('loads.requestedLoadsPageTitle')}</h2>
                <Link
                  to="/requestedloads"
                  className="text-sm text-blue-600 hover:text-blue-500"
                >
                  {t('dashboard.viewAll')}
                </Link>
              </div>

              {requestedLoadsLoading ? (
                <div className="space-y-3">
                  <CardLoader />
                  <CardLoader />
                  <CardLoader />
                </div>
              ) : requestedLoads.length > 0 ? (
                <div className="space-y-3">
                  {requestedLoads.slice(0, 5).map((load) => (
                    <button
                      type="button"
                      key={load.id}
                      onClick={() => openDriverLoadDetail(load)}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                    >
                      <div className="flex items-center space-x-3 flex-1">
                        <div className="p-2 bg-orange-100 rounded">
                          <ClockIcon className="h-4 w-4 text-orange-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <p className="text-sm font-medium text-gray-900 truncate">
                              {load.truck?.plateNumber || '—'}
                            </p>
                            <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full ml-2 bg-yellow-100 text-yellow-800 flex-shrink-0">
                              {t('loads.statuses.requested')}
                            </span>
                          </div>
                          <p className="text-xs text-gray-500">
                            {load.product?.name || '—'} • {load.port?.name || '—'}
                          </p>
                          <p className="text-xs text-gray-400">
                            {load.loadDate ? formatDate(load.loadDate) : '—'}
                          </p>
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              ) : null}
            </div>
            )}

            {isOperator() && clientDashboardDdps.length > 0 && (
            <div className="card">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-medium text-gray-900">Demandes de Pesee (DDP)</h2>
                <Link
                  to="/ddp/list"
                  className="text-sm text-blue-600 hover:text-blue-500"
                >
                  {t('dashboard.viewAll')}
                </Link>
              </div>

              {clientDdpsLoading ? (
                <div className="space-y-3">
                  <CardLoader />
                  <CardLoader />
                  <CardLoader />
                </div>
              ) : (
                <div className="space-y-3">
                  {clientDashboardDdps.slice(0, 5).map((ddp) => (
                    <Link
                      key={ddp.id}
                      to={`/ddp/${ddp.id}`}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                    >
                      <div className="flex items-center space-x-3 flex-1">
                        <div className={`p-2 rounded ${ddp.status === 'denied' ? 'bg-red-100' : 'bg-yellow-100'}`}>
                          <TicketIcon className={`h-4 w-4 ${ddp.status === 'denied' ? 'text-red-600' : 'text-yellow-600'}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <p className="text-sm font-medium text-gray-900 truncate">
                              {ddp.navire || '—'}
                            </p>
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ml-2 flex-shrink-0 ${
                              ddp.status === 'denied'
                                ? 'bg-red-100 text-red-800'
                                : 'bg-yellow-100 text-yellow-800'
                            }`}>
                              {t(`ddp.status.${ddp.status}`)}
                            </span>
                          </div>
                          <p className="text-xs text-gray-500">
                            {ddp.manutentionNumber || '—'} • {ddp.productsSummary || '—'}
                          </p>
                          <p className="text-xs text-gray-400">
                            {ddp.arrivingDate ? formatDate(ddp.arrivingDate) : '—'} • {Number(ddp.totalWeight || 0).toLocaleString('fr-FR')} t
                          </p>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </div>
            )}

            {/* Recent Loads - Only for clients */}
            {isOperator() && (
            <div className="card">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-medium text-gray-900">{t('dashboard.recentLoads')}</h2>
                <Link
                  to="/loads"
                  className="text-sm text-blue-600 hover:text-blue-500"
                >
                  {t('dashboard.viewAll')}
                </Link>
              </div>
              
              {loading ? (
                <div className="space-y-3">
                  <CardLoader />
                  <CardLoader />
                  <CardLoader />
                </div>
              ) : dashboardRecentWeighedLoads.length > 0 ? (
                <div className="space-y-3">
                  {dashboardRecentWeighedLoads.slice(0, 5).map((load) => (
                    <Link
                      key={load.id}
                      to="/loads"
                      state={{ openLoadId: load.id }}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors text-left no-underline text-inherit"
                    >
                      <div className="flex items-center space-x-3">
                        <div className="p-2 bg-blue-100 rounded">
                          <TruckIcon className="h-4 w-4 text-blue-600" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center justify-between">
                            <p className="text-sm font-medium text-gray-900">
                              {load.truck}
                            </p>
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ml-2 ${
                              load.status === 'LOADED' ? 'bg-green-100 text-green-800' :
                              load.status === 'ACCEPTED' ? 'bg-blue-100 text-blue-800' :
                              load.status === 'REQUESTED' ? 'bg-yellow-100 text-yellow-800' :
                              load.status === 'REJECTED' ? 'bg-red-100 text-red-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {t(`loads.statuses.${load.status?.toLowerCase()}`)}
                            </span>
                          </div>
                          <p className="text-xs text-gray-500">
                            {load.client} • {load.product}
                          </p>
                          <p className="text-xs text-gray-400">
                            {load.time} • {load.port}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-medium text-gray-900">
                          {load.weight ? `${(load.weight / 1000).toLocaleString('fr-FR', { maximumFractionDigits: 2, minimumFractionDigits: 2 })} Ton` : '-'}
                        </p>
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6 text-gray-500">
                  <TruckIcon className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                  <p>{t('dashboard.noRecentLoads')}</p>
                </div>
              )}
            </div>
            )}

            {/* Recent Bills */}
            <div className="card">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-medium text-gray-900">{t('dashboard.recentBills')}</h2>
                <Link
                  to="/bills"
                  className="text-sm text-blue-600 hover:text-blue-500"
                >
                  {t('dashboard.viewAll')}
                </Link>
              </div>
              
              {recentBillsLoading ? (
                <div className="space-y-3">
                  <CardLoader />
                  <CardLoader />
                  <CardLoader />
                </div>
              ) : recentBillsData?.recentBills?.length > 0 ? (
                <div className="space-y-3">
                  {recentBillsData.recentBills.map((bill) => (
                    <Link
                      key={bill.id}
                      to="/bills"
                      state={{ openBillId: bill.id }}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors text-left no-underline text-inherit"
                    >
                      <div className="flex items-center space-x-3 min-w-0 flex-1">
                        <div className="p-2 bg-green-100 rounded">
                          <FcfaIcon className="h-4 w-4 text-green-600" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center justify-between">
                            <p className="text-sm font-medium text-gray-900">
                              {bill.billNumber}
                            </p>
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ml-2 ${
                              bill.status === 'PAID' ? 'bg-green-100 text-green-800' :
                              bill.status === 'PENDING' ? 'bg-yellow-100 text-yellow-800' :
                              bill.status === 'OVERDUE' ? 'bg-red-100 text-red-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {t(`bills.${bill.status?.toLowerCase()}`)}
                            </span>
                          </div>
                          <p className="text-xs text-gray-500">
                            {bill.certificate?.client?.companyName} • {bill.certificate?.product?.name}
                          </p>
                          <p className="text-xs text-gray-400">
                            {formatDate(bill.generatedDate)}
                            {bill.payments?.[0] && (
                              <span className={`ml-2 inline-flex px-1 py-0.5 text-xs font-medium rounded ${
                                bill.payments[0].status === 'COMPLETED' ? 'bg-green-100 text-green-700' :
                                bill.payments[0].status === 'WAITING_FOR_VERIFICATION' ? 'bg-orange-100 text-orange-700' :
                                'bg-gray-100 text-gray-700'
                              }`}>
                                {t(paymentStatusI18nKey(bill.payments[0], bill.payments), {
                                  defaultValue: bill.payments[0].status,
                                })}
                              </span>
                            )}
                          </p>
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <p className="text-sm font-medium text-gray-900">
                          {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'XOF' }).format(bill.totalAmount)}
                        </p>
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6 text-gray-500">
                  <FcfaIcon className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                  <p>{t('dashboard.noRecentBills')}</p>
                </div>
              )}
            </div>

            {/* Recent Payments - Only for Accountants */}
            {isAccountant() && (
            <div className="card">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-medium text-gray-900">{t('dashboard.recentPayments')}</h2>
                <Link
                  to="/payments"
                  className="text-sm text-blue-600 hover:text-blue-500"
                >
                  {t('dashboard.viewAll')}
                </Link>
              </div>
              
              {recentPaymentsLoading ? (
                <div className="space-y-3">
                  <CardLoader />
                  <CardLoader />
                  <CardLoader />
                </div>
              ) : recentPaymentsData?.recentPayments?.length > 0 ? (
                <div className="space-y-3">
                  {recentPaymentsData.recentPayments.map((payment) => (
                    <Link
                      key={payment.id}
                      to="/payments"
                      state={{ openPaymentId: payment.id }}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors text-left no-underline text-inherit"
                    >
                      <div className="flex items-center space-x-3 min-w-0 flex-1">
                        <div className="p-2 bg-blue-100 rounded">
                          <CheckCircleIcon className="h-4 w-4 text-blue-600" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center justify-between">
                            <p className="text-sm font-medium text-gray-900">
                              {payment.bill?.billNumber}
                            </p>
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ml-2 ${
                              payment.status === 'COMPLETED' ? 'bg-green-100 text-green-800' :
                              payment.status === 'WAITING_FOR_VERIFICATION' ? 'bg-orange-100 text-orange-800' :
                              payment.status === 'PENDING' ? 'bg-yellow-100 text-yellow-800' :
                              payment.status === 'FAILED' ? 'bg-red-100 text-red-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {t(paymentStatusI18nKey(payment), { defaultValue: payment.status })}
                            </span>
                          </div>
                          <p className="text-xs text-gray-500">
                            {payment.bill?.certificate?.client?.companyName} • {payment.method}
                          </p>
                          <p className="text-xs text-gray-400">
                            {formatDate(payment.processedDate)}
                          </p>
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <p className="text-sm font-medium text-gray-900">
                          {new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'XOF' }).format(payment.amount)}
                        </p>
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6 text-gray-500">
                  <CheckCircleIcon className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                  <p>{t('dashboard.noRecentPayments')}</p>
                </div>
              )}
            </div>
            )}
          </>
        ) : isDriver() ? (
          // Driver Dashboard
          <>
            {/* GPS Activation for Drivers - only if tracking module is enabled */}
            {isTrackingEnabled && <GPSActivation />}
            
            {/* Driver-specific loads table */}
            <div className="card">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-medium text-gray-900">{t('dashboard.myLoadsInProgress')}</h2>
              <Link
                to="/loads"
                className="text-sm text-blue-600 hover:text-blue-500"
              >
                {t('dashboard.viewAll')}
              </Link>
            </div>
            
            {/* Filters */}
            <div className="mb-4 flex flex-col sm:flex-row gap-4">
              <div className="flex-1">
                <div className="relative">
                  <MagnifyingGlassIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <Input
                    type="text"
                    placeholder={t('dashboard.searchLoads')}
                    value={searchTerm}
                    onChange={handleSearch}
                    className="pl-10"
                  />
                </div>
              </div>
              <div className="sm:w-48">
                <div className="relative">
                  <FunnelIcon className="h-5 w-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                  <select
                    value={statusFilter}
                    onChange={handleStatusFilter}
                    className="input w-full pl-10"
                  >
                    <option value="">{t('dashboard.allInProgress')}</option>
                    <option value="REQUESTED">{t('loads.statuses.requested')}</option>
                    <option value="REJECTED">{t('loads.statuses.rejected')}</option>
                    <option value="ACCEPTED">{t('loads.statuses.accepted')}</option>
                    <option value="LOADED">{t('loads.statuses.loaded')}</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Driver Loads Table */}
            {!shouldFetchLoads ? (
              <div className="text-center py-8 text-gray-500">
                <TruckIcon className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                <p>{t('dashboard.noTruckAssigned')}</p>
              </div>
            ) : loadsLoading ? (
              <div className="py-8">
                <LoadingSpinner />
              </div>
            ) : (
              <Table
                columns={driverLoadsColumns}
                data={Array.isArray(driverLoadsData) ? driverLoadsData : []}
                pagination={shouldFetchLoads && driverLoadsResult.pagination ? {
                  page: driverLoadsResult.pagination.page || 1,
                  limit: driverLoadsResult.pagination.limit || 10,
                  total: driverLoadsResult.pagination.total || 0,
                  totalPages: driverLoadsResult.pagination.totalPages || 1,
                  onChange: driverLoadsResult.goToPage
                } : undefined}
                emptyState={{
                  icon: ArchiveBoxIcon,
                  title: "No loads found",
                  description: "No loads match your current filters."
                }}
              />
            )}
          </div>
          </>
        ) : (
          // Manager/Admin: Show Opened DDP + Recent Loads
          <>
            {isPeseur() && (peseurLoadsLoading || peseurRequestedLoads.length > 0) && (
              <div className="card">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-medium text-gray-900">{t('loads.requestedLoadsPageTitle')}</h2>
                  <Link
                    to="/requestedloads"
                    className="text-sm text-blue-600 hover:text-blue-500"
                  >
                    {t('dashboard.viewAll')}
                  </Link>
                </div>

                {peseurLoadsLoading ? (
                  <div className="space-y-3">
                    <CardLoader />
                    <CardLoader />
                    <CardLoader />
                  </div>
                ) : peseurRequestedLoads.length > 0 ? (
                  <div className="space-y-3">
                    {peseurRequestedLoads.map((load) => (
                      <button
                        type="button"
                        key={load.id}
                        onClick={() => openDriverLoadDetail(load)}
                        className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                      >
                        <div className="flex items-center space-x-3 flex-1">
                          <div className="p-2 bg-orange-100 rounded">
                            <ClockIcon className="h-4 w-4 text-orange-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between">
                              <p className="text-sm font-medium text-gray-900 truncate">
                                {load.truck?.plateNumber || '—'}
                              </p>
                              <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full ml-2 bg-yellow-100 text-yellow-800 flex-shrink-0">
                                {t('loads.statuses.requested')}
                              </span>
                            </div>
                            <p className="text-xs text-gray-500">
                              {load.client?.companyName || '—'} • {load.product?.name || '—'}
                            </p>
                            <p className="text-xs text-gray-400">
                              {load.loadDate ? formatDate(load.loadDate) : '—'}
                            </p>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                ) : null}
              </div>
            )}

            {isPeseur() && peseurCertificatesNeedingAttention.length > 0 && (
              <div className="card">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-medium text-gray-900">
                    {t('dashboard.peseurCertificatesAttention') || 'Certificates awaiting certification or rejected'}
                  </h2>
                  <Link
                    to="/certificates"
                    className="text-sm text-blue-600 hover:text-blue-500"
                  >
                    {t('dashboard.viewAll')}
                  </Link>
                </div>

                {peseurCertificatesLoading ? (
                  <div className="space-y-3">
                    <CardLoader />
                    <CardLoader />
                    <CardLoader />
                  </div>
                ) : (
                  <div className="space-y-3">
                    {peseurCertificatesNeedingAttention.slice(0, 5).map((certificate) => {
                      const status = String(certificate.status || '').toUpperCase();
                      const badgeClass =
                        status === 'REJECTED'
                          ? 'bg-red-100 text-red-800'
                          : 'bg-amber-100 text-amber-800';
                      const iconClass =
                        status === 'REJECTED'
                          ? 'bg-red-100 text-red-600'
                          : 'bg-amber-100 text-amber-600';

                      return (
                        <Link
                          key={certificate.id}
                          to="/certificates"
                          className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                        >
                          <div className="flex items-center space-x-3 flex-1">
                            <div className={`p-2 rounded ${iconClass}`}>
                              <CheckBadgeIcon className="h-4 w-4" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between">
                                <p className="text-sm font-medium text-gray-900 truncate">
                                  {certificate.certificateNumber || '—'}
                                </p>
                                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ml-2 flex-shrink-0 ${badgeClass}`}>
                                  {status === 'REJECTED'
                                    ? t('certificates.statusRejected', { defaultValue: 'Rejete' })
                                    : t('certificates.statusPending', { defaultValue: 'En attente' })}
                                </span>
                              </div>
                              <p className="text-xs text-gray-500">
                                {certificate.client?.companyName || '—'} • {certificate.product?.name || '—'}
                              </p>
                              <p className="text-xs text-gray-400">
                                {certificate.date ? formatDate(certificate.date) : '—'} • {formatWeight(certificate.totalWeight || 0, 'ton', { noUnit: true })}
                              </p>
                            </div>
                          </div>
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {managerOpsDashboard && pendingCertificates.length > 0 && (
              <div className="card">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-medium text-gray-900">
                    {t('dashboard.certificatesToCertify') || 'Certificates to certify'}
                  </h2>
                  <Link
                    to="/certificates"
                    className="text-sm text-blue-600 hover:text-blue-500"
                  >
                    {t('dashboard.viewAll')}
                  </Link>
                </div>

                {pendingCertificatesLoading ? (
                  <div className="space-y-3">
                    <CardLoader />
                    <CardLoader />
                    <CardLoader />
                  </div>
                ) : (
                  <div className="space-y-3">
                    {pendingCertificates.slice(0, 5).map((certificate) => (
                      <Link
                        key={certificate.id}
                        to="/certificates"
                        state={{ openCertificateId: certificate.id }}
                        className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                      >
                        <div className="flex items-center space-x-3 flex-1">
                          <div className="p-2 bg-amber-100 rounded">
                            <CheckBadgeIcon className="h-4 w-4 text-amber-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between">
                              <p className="text-sm font-medium text-gray-900 truncate">
                                {certificate.certificateNumber || '—'}
                              </p>
                              <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full ml-2 bg-amber-100 text-amber-800 flex-shrink-0">
                                {t('dashboard.pendingStatus')}
                              </span>
                            </div>
                            <p className="text-xs text-gray-500">
                              {certificate.client?.companyName || '—'} • {certificate.product?.name || '—'}
                            </p>
                            <p className="text-xs text-gray-400">
                              {certificate.date ? formatDate(certificate.date) : '—'} • {formatWeight(certificate.totalWeight || 0, 'ton', { noUnit: true })}
                            </p>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            )}

            {managerOpsDashboard && (requestedLoadsLoading || requestedLoads.length > 0) && (
              <div className="card">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-medium text-gray-900">{t('loads.requestedLoadsPageTitle')}</h2>
                  <Link
                    to="/requestedloads"
                    className="text-sm text-blue-600 hover:text-blue-500"
                  >
                    {t('dashboard.viewAll')}
                  </Link>
                </div>

                {requestedLoadsLoading ? (
                  <div className="space-y-3">
                    <CardLoader />
                    <CardLoader />
                    <CardLoader />
                  </div>
                ) : requestedLoads.length > 0 ? (
                  <div className="space-y-3">
                    {requestedLoads.slice(0, 5).map((load) => (
                      <button
                        type="button"
                        key={load.id}
                        onClick={() => openDriverLoadDetail(load)}
                        className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                      >
                        <div className="flex items-center space-x-3 flex-1">
                          <div className="p-2 bg-orange-100 rounded">
                            <ClockIcon className="h-4 w-4 text-orange-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between">
                              <p className="text-sm font-medium text-gray-900 truncate">
                                {load.truck?.plateNumber || '—'}
                              </p>
                              <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full ml-2 bg-yellow-100 text-yellow-800 flex-shrink-0">
                                {t('loads.statuses.requested')}
                              </span>
                            </div>
                            <p className="text-xs text-gray-500">
                              {load.client?.companyName || '—'} • {load.product?.name || '—'}
                            </p>
                            <p className="text-xs text-gray-400">
                              {load.loadDate ? formatDate(load.loadDate) : '—'}
                            </p>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                ) : null}
              </div>
            )}

            {/* Opened DDP section - for managers only */}
            {managerOpsDashboard && filteredPendingDdps.length > 0 && (
              <div className="card">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-medium text-gray-900">{t('dashboard.openedDdp') || 'Opened DDP'}</h2>
                  <Link
                    to="/ddp/list"
                    className="text-sm text-blue-600 hover:text-blue-500"
                  >
                    {t('dashboard.viewAll')}
                  </Link>
                </div>
                
                {pendingDdpsLoading ? (
                  <div className="space-y-3">
                    <CardLoader />
                    <CardLoader />
                    <CardLoader />
                  </div>
                ) : filteredPendingDdps?.length > 0 ? (
                  <div className="space-y-3">
                    {filteredPendingDdps.slice(0, 5).map((ddp) => (
                      <Link
                        key={ddp.id}
                        to={`/ddp/${ddp.id}`}
                        className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                      >
                        <div className="flex items-center space-x-3 flex-1">
                          <div className="p-2 bg-yellow-100 rounded">
                            <TicketIcon className="h-4 w-4 text-yellow-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between">
                              <p className="text-sm font-medium text-gray-900 truncate">
                                {ddp.navire}
                              </p>
                              <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full ml-2 bg-yellow-100 text-yellow-800 flex-shrink-0">
                                {t('ddp.status.pending')}
                              </span>
                            </div>
                            <p className="text-xs text-gray-500">
                              {ddp.operator?.assignedClient?.companyName || `${ddp.operator?.firstName || ''} ${ddp.operator?.lastName || ''}`.trim() || ddp.operator?.username || '—'} • {ddp.manutentionNumber || '—'}
                            </p>
                            <p className="text-xs text-gray-400">
                              {ddp.arrivingDate ? formatDate(ddp.arrivingDate) : '—'} • {ddp.productsSummary || '—'} • {Number(ddp.totalWeight || 0).toLocaleString('fr-FR')} t
                            </p>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-6 text-gray-500">
                    <CheckCircleIcon className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                    <p>{t('dashboard.noPendingDdps') || 'No pending DDPs'}</p>
                  </div>
                )}
              </div>
            )}
            
            {/* DOP/OOP section - for managers only */}
            {managerOpsDashboard && dashboardOops.length > 0 && (
              <div className="card">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-lg font-medium text-gray-900">{t('dashboard.openedDop') || 'ODP ouverts'}</h2>
                  <Link
                    to="/requestedloads"
                    className="text-sm text-blue-600 hover:text-blue-500"
                  >
                    {t('dashboard.viewAll')}
                  </Link>
                </div>
                
                {pendingOopsLoading ? (
                  <div className="space-y-3">
                    <CardLoader />
                    <CardLoader />
                    <CardLoader />
                  </div>
                ) : dashboardOops?.length > 0 ? (
                  <div className="space-y-3">
                    {dashboardOops.slice(0, 5).map((oop) => (
                      <Link
                        key={oop.id}
                        to="/requestedloads"
                        className="w-full text-left flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                      >
                        <div className="flex items-center space-x-3 flex-1">
                          <div className="p-2 bg-blue-100 rounded">
                            <TicketIcon className="h-4 w-4 text-blue-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between">
                              <p className="text-sm font-medium text-gray-900 truncate">
                                {oop.operationNum || '—'}
                              </p>
                              <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full ml-2 bg-blue-100 text-blue-800 flex-shrink-0">
                                DOP
                              </span>
                            </div>
                            <p className="text-xs text-gray-500">
                              {oop.ddp?.navire || '—'} • {oop.ddp?.manutentionNumber || '—'}
                            </p>
                            <p className="text-xs text-gray-400">
                              {oop.ddp?.port?.name || '—'} • {oop.createdAt ? formatDate(oop.createdAt) : '—'}
                            </p>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-6 text-gray-500">
                    <CheckCircleIcon className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                    <p>{t('dashboard.noOpenedDop') || 'Aucun DOP ouvert'}</p>
                  </div>
                )}
              </div>
            )}
            
            {/* Regular recent loads for non-drivers (hidden for admins — no /loads access) */}
            {!isAdmin() && (
            <div className="card">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-medium text-gray-900">{t('dashboard.recentLoads')}</h2>
              <Link
                to="/loads"
                className="text-sm text-blue-600 hover:text-blue-500"
              >
                {t('dashboard.viewAll')}
              </Link>
            </div>
            
            {(isPeseur() ? peseurStats.recentLoads.length > 0 : dashboardRecentWeighedLoads.length > 0) ? (
              <div className="space-y-3">
                {(isPeseur() ? peseurStats.recentLoads : dashboardRecentWeighedLoads).map((load) => {
                  const rowInner = (
                    <>
                      <div className="flex items-center space-x-3">
                        <div className="p-2 bg-blue-100 rounded">
                          <TruckIcon className="h-4 w-4 text-blue-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-2">
                            <p className="text-sm font-medium text-gray-900 truncate">
                              {load.truck} - {load.client}
                            </p>
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ml-2 flex-shrink-0 ${getStatusBadgeColor(load?.status)}`}>
                              {load?.status ? t(`loads.statuses.${load.status.toLowerCase()}`) : t('dashboard.unknown')}
                            </span>
                          </div>
                          <p className="text-xs text-gray-500">
                            {load.product} • {load.time}
                          </p>
                          {isPeseur() && (
                            <p className="text-xs text-blue-600 font-medium mt-0.5">{t('dashboard.viewLoadDetails')}</p>
                          )}
                        </div>
                      </div>
                      <div className="text-right flex-shrink-0">
                        <p className="text-sm font-medium text-gray-900">
                          {formatWeight(load.weight, 'ton', { noUnit: true })}
                        </p>
                        <p className="text-xs text-gray-500">
                          {load.type}
                        </p>
                      </div>
                    </>
                  );

                  return (
                    <Link
                      key={load.id}
                      to="/loads"
                      state={{ openLoadId: load.id }}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors text-left no-underline text-inherit"
                    >
                      {rowInner}
                    </Link>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-6 text-gray-500">
                <ArchiveBoxIcon className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                <p>{t('dashboard.noRecentLoads')}</p>
              </div>
            )}
          </div>
            )}
          </>
        )}

        {/* System Overview - Hidden for Drivers, Accountants, and Clients */}
        {!isDriver() && !isAccountant() && !isOperator() && !isPeseur() && (
          <div className="card">
            <h2 className="text-lg font-medium text-gray-900 mb-4">{t('dashboard.systemOverview')}</h2>
            
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <TruckIcon className="h-5 w-5 text-gray-400" />
                  <span className="text-sm text-gray-600">{t('dashboard.activeTrucks')}</span>
                </div>
                <span className="text-sm font-medium">{stats.totals?.trucks || 0}</span>
              </div>
              
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <BuildingOfficeIcon className="h-5 w-5 text-gray-400" />
                  <span className="text-sm text-gray-600">{t('dashboard.totalClients')}</span>
                </div>
                <span className="text-sm font-medium">{stats.totals?.clients || 0}</span>
              </div>
              
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <ArchiveBoxIcon className="h-5 w-5 text-gray-400" />
                  <span className="text-sm text-gray-600">{t('dashboard.totalProducts')}</span>
                </div>
                <span className="text-sm font-medium">{stats.totals?.products || 0}</span>
              </div>
              
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <FcfaIcon className="h-5 w-5 text-gray-400" />
                  <span className="text-sm text-gray-600">{t('dashboard.totalBills')}</span>
                </div>
                <span className="text-sm font-medium">{stats.totals?.bills || 0}</span>
              </div>

              <div className="border-t pt-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-gray-900">{t('dashboard.weeklyActivity')}</span>
                </div>
                <div className="mt-2 space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">{t('dashboard.loadsThisWeek')}</span>
                    <span className="font-medium">{stats.weekStats?.loads || 0}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">{t('dashboard.weightThisWeek')}</span>
                    <span className="font-medium">{formatWeight(stats.weekStats?.weight || 0, 'ton', { noUnit: true })}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Quick Actions (moved to bottom) */}
        <div className="lg:col-span-2">
          <h2 className="text-lg font-medium text-gray-900 mb-4">{t('common.actions')}</h2>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">

            {!isAccountant() && !isAdmin() && (
              <QuickAction
                title={isDriver() ? t('navigation.myLoads') : t('loads.title')}
                description={isDriver() ? t('dashboard.myTruckLoads') : t('loads.allLoads')}
                to="/loads"
                icon={ArchiveBoxIcon}
                color="green"
              />
            )}

            {!isDriver() && !isAccountant() && !isAdmin() && (
              <QuickAction
                title={t('certificates.viewMyCertificates')}
                description={t('certificates.myCertificatesDesc')}
                to="/certificates"
                icon={TicketIcon}
                color="yellow"
              />
            )}
            
            {!isDriver() && !isAccountant() && (
              <QuickAction
                title={t('reports.title')}
                description={t('reports.dailyReport')}
                to="/reports"
                icon={ChartBarIcon}
                color="red"
              />
            )}
          </div>
        </div>

        <Modal
          isOpen={isClientApprovalModalOpen}
          onClose={() => {
            if (approvingClient) return;
            setIsClientApprovalModalOpen(false);
            setSelectedPendingClient(null);
            setSelectedPendingClientDetail(null);
            setSelectedTransporteurIds([]);
            setCreateUserOnApproval(true);
          }}
          title={t('dashboard.clientApprovalTitle') || 'Client approval'}
          size="md"
          footer={
            <>
              <Button
                variant="outline"
                onClick={() => {
                  if (approvingClient) return;
                  setIsClientApprovalModalOpen(false);
                  setSelectedPendingClient(null);
                  setSelectedPendingClientDetail(null);
                  setSelectedTransporteurIds([]);
                  setCreateUserOnApproval(true);
                }}
                disabled={approvingClient}
              >
                {t('common.cancel')}
              </Button>
              <Button
                variant="success"
                onClick={handleApproveClient}
                loading={approvingClient}
                disabled={!selectedPendingClient || selectedTransporteurIds.length === 0}
              >
                {t('dashboard.approveClient') || 'Approve client'}
              </Button>
            </>
          }
        >
          {selectedPendingClient ? (
            <div className="space-y-3">
              {loadingPendingClientDetail && (
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <LoadingSpinner size="sm" />
                  <span>{t('common.loading')}</span>
                </div>
              )}
              <div>
                <p className="text-xs text-gray-500">{t('clients.companyName')}</p>
                <p className="text-sm font-medium text-gray-900">{selectedPendingClientDetail?.companyName || selectedPendingClient.companyName || '—'}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">{t('clients.contactName')}</p>
                <p className="text-sm text-gray-900">{selectedPendingClientDetail?.contactName || selectedPendingClient.contactName || '—'}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">{t('clients.contactEmail')}</p>
                <p className="text-sm text-gray-900">{selectedPendingClientDetail?.contactEmail || selectedPendingClient.contactEmail || '—'}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">{t('clients.contactPhone')}</p>
                <p className="text-sm text-gray-900">{selectedPendingClientDetail?.contactPhone || selectedPendingClient.contactPhone || '—'}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">{t('clients.status')}</p>
                <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-amber-100 text-amber-800">
                  {t(`clients.${selectedPendingClientDetail?.status || selectedPendingClient?.status || 'pending'}`)}
                </span>
              </div>

              {(() => {
                const clientCompanyNorm = (selectedPendingClientDetail?.companyName || selectedPendingClient?.companyName || '').trim().toLowerCase();
                const hasSameClientTransporteur = clientCompanyNorm
                  ? transporteurOptions.some(option => (option.label || '').trim().toLowerCase() === clientCompanyNorm)
                  : false;
                const showGenerateTransporteurButton = (selectedPendingClientDetail?.status || selectedPendingClient?.status) === 'pending' && !hasSameClientTransporteur;

                return (
                  <div>
                    <div className="flex flex-wrap items-end gap-2">
                      <div className="flex-1 min-w-[220px]">
                        <MultiSelect
                          label={t('dashboard.transporteursForApproval') || 'Transporteurs for approval'}
                          values={selectedTransporteurIds}
                          onChange={(event) => setSelectedTransporteurIds(event.target.value || [])}
                          options={transporteurOptions}
                          placeholder={t('dashboard.selectTransporteursForApproval') || 'Select at least one transporteur'}
                          disabled={transporteursLoading || loadingPendingClientDetail || generateTransporteurLoading}
                        />
                      </div>
                      {showGenerateTransporteurButton && (
                        <Button
                          type="button"
                          variant="primary"
                          onClick={handleGenerateTransporteurFromClient}
                          loading={generateTransporteurLoading}
                          disabled={generateTransporteurLoading || transporteursLoading || loadingPendingClientDetail}
                          className="shrink-0"
                        >
                          {t('clients.generateTransporteur')}
                        </Button>
                      )}
                    </div>
                    {showGenerateTransporteurButton && (
                      <p className="text-xs text-gray-500 mt-1">
                        {t('clients.generateTransporteurHint')}
                      </p>
                    )}
                  </div>
                );
              })()}

              {selectedTransporteurIds.length === 0 && (
                <p className="text-xs text-amber-700">
                  {t('dashboard.transporteurRequiredForActivation') || 'At least one transporteur is required to approve and activate this client.'}
                </p>
              )}

              {(() => {
                const hasPendingPassword = selectedPendingClientDetail?.hasPendingPassword || selectedPendingClient?.hasPendingPassword;
                if (!hasPendingPassword) return null;
                return (
                  <div className="pt-2 border-t border-gray-200">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={createUserOnApproval}
                        onChange={(e) => setCreateUserOnApproval(e.target.checked)}
                        className="checkbox-app"
                        disabled={approvingClient}
                      />
                      <span className="text-sm text-gray-700">
                        {t('dashboard.createUserOnApproval') || 'Create user account automatically'}
                      </span>
                    </label>
                    <p className="text-xs text-gray-500 mt-1 ml-6">
                      {t('dashboard.createUserOnApprovalHint') || 'The client registered with a password. Check this to create their login account automatically.'}
                    </p>
                  </div>
                );
              })()}
            </div>
          ) : null}
        </Modal>

        <LoadDetailViewModal
          isOpen={driverLoadDetailOpen}
          onClose={() => {
            setDriverLoadDetailOpen(false);
            setDriverLoadDetail(null);
          }}
          load={driverLoadDetail}
          loading={driverLoadDetailLoading}
          onLoadUpdated={() => {
            if (typeof refetchRequestedLoads === 'function') {
              refetchRequestedLoads();
            }
          }}
        />

        <LoadSupplementaryTaxModal
          isOpen={showTsModal}
          onClose={handleDashboardTsClose}
          onConfirm={handleDashboardTsConfirm}
          loading={updateLoading}
        />
      </div>
    </div>
  );
};

export default DashboardPage;